import { useTranslate } from "@similarweb/i18n-provider";
import { Alerts } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Text } from "@similarweb/ui-components/foundations";

import { useIsIndustryTaxonomyOptedIn } from "app/pages/sales-intelligence/modules/industry/hooks/useIsIndustryTaxonomyOptedIn";
import useCrmJobsTransitionQuery from "app/pages/sales-intelligence/modules/industry/queries/useCrmJobsTransitionQuery";
import type { CrmJobsTransitionProvider } from "app/pages/sales-intelligence/modules/industry/types/crmJobsTransition";
import {
    hasCrmJobsTransition,
    hasFieldMappingsTransition,
} from "app/pages/sales-intelligence/modules/industry/utils/crmJobsTransition";
import { industryTransitionKeys } from "app/pages/sales-intelligence/modules/industry/utils/i18n";

import { StyledCrmIndustryTransitionAlertBanner, StyledCrmIndustryTransitionAlertBannerIcon } from "./styles";

export type CrmIndustryTransitionAlertBannerVariant = "fieldMappings" | "crmJobs";

type Props = {
    crm: CrmJobsTransitionProvider;
    variant: CrmIndustryTransitionAlertBannerVariant;
};

const VARIANT_CONFIG = {
    fieldMappings: {
        translationKey: industryTransitionKeys.crmManagementFieldMappingsBanner,
        dataAutomationSuffix: "field-mappings-transition-banner",
    },
    crmJobs: {
        translationKey: industryTransitionKeys.crmManagementEnrichmentJobsBanner,
        dataAutomationSuffix: "enrichment-jobs-transition-banner",
    },
} as const;

export const CrmIndustryTransitionAlertBanner = ({ crm, variant }: Props) => {
    const { translate } = useTranslate();
    const isIndustryTaxonomyOptedIn = useIsIndustryTaxonomyOptedIn();
    const { data } = useCrmJobsTransitionQuery();

    const isVisible =
        isIndustryTaxonomyOptedIn &&
        (variant === "fieldMappings" ? hasFieldMappingsTransition(data, crm) : hasCrmJobsTransition(data, crm));

    if (!isVisible) {
        return null;
    }

    const { translationKey, dataAutomationSuffix } = VARIANT_CONFIG[variant];

    return (
        <StyledCrmIndustryTransitionAlertBanner
            alignItems="center"
            data-automation={`${crm}-${dataAutomationSuffix}`}
        >
            <StyledCrmIndustryTransitionAlertBannerIcon alignItems="center">
                <Alerts
                    size="sm"
                    color={colorsPalettes.carbon["200"]}
                />
            </StyledCrmIndustryTransitionAlertBannerIcon>
            <Text
                fontVariant="body1"
                color={colorsPalettes.carbon["400"]}
                data-automation={`${crm}-${dataAutomationSuffix}-text`}
            >
                {translate(translationKey)}
            </Text>
        </StyledCrmIndustryTransitionAlertBanner>
    );
};
